import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { CircularProgress, useTheme } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

import FilterCategory from '../../add-project-policy/left-nav/filter-category';
import FilterHeader from '../../add-project-policy/left-nav/filters-header';
import useProjectPolicyLeftNavContext from '../../add-project-policy/left-nav/left-nav-context';
import { getDesignTokens } from '../../base/core';

import {
  buildPolicyCategoriesFromInsights,
  usePolicyInsightCategories,
} from '~/client/hooks/policy';
import * as GQL from '~/graphql/generated/client';
import { QuickActionDrawer } from '~c/components/base/quick-action';

interface Props {
  className?: string;
}

const GovernanceRightNavImpl: React.FC<Props> = ({ className }) => {
  const theme = useTheme();
  const tokens = React.useMemo(
    () =>
      getDesignTokens(
        theme.palette.mode === 'dark' ? 'dark' : 'light',
        theme.accentPalette
      ),
    [theme.palette.mode]
  );
  const {
    state: { filters },
    setFilter,
    clearFilters,
  } = useProjectPolicyLeftNavContext();

  // Use shared hook to fetch insights + categories
  const { categories, loading, error } = usePolicyInsightCategories();

  const isFilterSelected = (name: string) => {
    const found = filters.find((filter) => filter.name === name);
    return found ? found.selected : false;
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <QuickActionDrawer
        header={{
          text: '',
        }}
        fabIcon={<FilterAltIcon />}
        sx={{
          '& .v2-grid': {
            padding: 0,
          },
        }}
      >
        <div style={{ padding: 20, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <div
            style={{
              marginTop: 8,
              fontSize: 14,
              color: tokens.textMuted,
            }}
          >
            Loading policy options...
          </div>
        </div>
      </QuickActionDrawer>
    );
  }

  // Show error state if API fails
  if (error) {
    return (
      <QuickActionDrawer
        header={{
          text: '',
        }}
        fabIcon={<FilterAltIcon />}
        sx={{
          '& .v2-grid': {
            padding: 0,
          },
        }}
      >
        <div style={{ padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: tokens.status.danger }}>
            Failed to load policy options. Please try again later.
          </div>
        </div>
      </QuickActionDrawer>
    );
  }

  return (
    <QuickActionDrawer
      header={{
        text: '',
      }}
      fabIcon={<FilterAltIcon />}
      sx={{
        '& .v2-grid': {
          padding: 0,
        },
      }}
    >
      <div style={{ padding: 0, width: '100%' }}>
        <FilterHeader
          onClearFilters={clearFilters}
          sx={{
            mt: 0,
            mb: 5,
            '& .MuiTypography-root': {
              color: `${tokens.textPrimary} !important`,
            },
          }}
        />
        {categories.map((category) => (
          <FilterCategory
            key={category.name}
            categoryName={category.name}
            filters={category.labels.map((label) => ({
              name: label,
              isSelected: isFilterSelected(label),
              onSelect: (selected) => setFilter({ name: label, selected }),
            }))}
            sx={{
              marginBottom: 1,
              width: '100%',
              '& .MuiAccordionSummary-root': {
                minHeight: '80px !important',
                padding: '0 16px',
                backgroundColor: `${tokens.surface1} !important`,
                width: '100%',
                '& .MuiTypography-root': {
                  color: tokens.textPrimary,
                },
                '& .MuiSvgIcon-root': {
                  color: tokens.textPrimary,
                },
              },
              '& .MuiAccordionDetails-root': {
                padding: '8px 16px',
                backgroundColor: `${tokens.surface1} !important`,
                width: '100%',
                '& .MuiFormGroup-root': {
                  width: 'calc(100% - 16px)',
                },
              },
              '& .MuiFormControlLabel-root': {
                margin: 0,
                padding: '8px 0',
                minHeight: 'auto',
                width: '100%',
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  color: tokens.textPrimary,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: '1.4',
                  paddingRight: '16px',
                },
              },
              '& .MuiCheckbox-root': {
                padding: '8px',
                color: tokens.textPrimary,
              },
            }}
          />
        ))}
      </div>
    </QuickActionDrawer>
  );
};

const GovernanceRightNav = muiStyled(GovernanceRightNavImpl)(({ theme }) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );
  return css`
    .MuiButtonBase-root.MuiButton-root {
      width: 100%;
    }

    .MuiAccordion-root {
      margin: 0 !important;
      &:before {
        display: none;
      }
    }

    .v2-header {
      .MuiTypography-root {
        color: ${t.textPrimary} !important;
      }
      .MuiIconButton-root .MuiSvgIcon-root {
        color: ${t.textPrimary} !important;
      }
    }
  `;
});

export default GovernanceRightNav;
